import { useEffect, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { EditLayer } from '@/admin/AdminShell'
import { ApiError } from '@/lib/api'
import { fetchReplyTarget, type ReplyTarget } from './api'
import { WriteEmailDialog } from './WriteEmail'

/**
 * «Ответить» на входящее письмо в ленте (тикет #134).
 *
 * Ответ пишется с карточки, к которой привязано само письмо, — её, адрес отправителя и «Re:»
 * темы называет сервер (`/mail/inbound/{id}/reply`), а не лента: в ленте клиента лежат и письма
 * его контактов, и угадывать карточку на клиенте значило бы однажды ответить не с той. В окне
 * есть «Черновик ответа»; уходит письмо обычной кнопкой и ложится в тред исходного.
 *
 * Без своего клиента запросов, пока окно закрыто: ленту рисуют и экраны, где его нет.
 */
export function InboundReplyButton({ inboundId }: { inboundId: string }) {
  const { t, user } = useApp()
  const [open, setOpen] = useState(false)
  if (!can(user, 'sales.activity', 'create')) return null
  return (
    <>
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(true)}>
        {t('mail.reply.button')}
      </button>
      {open && <ReplyEmailDialog inboundId={inboundId} onClose={() => setOpen(false)} />}
    </>
  )
}

export function ReplyEmailDialog({ inboundId, onClose }: { inboundId: string; onClose: () => void }) {
  const { t } = useApp()
  const [target, setTarget] = useState<ReplyTarget | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    fetchReplyTarget(inboundId, controller.signal)
      .then(setTarget)
      .catch((failure: unknown) => {
        if (!controller.signal.aborted) setError(failure instanceof ApiError ? failure.message : t('app.error'))
      })
    return () => controller.abort()
  }, [inboundId, t])

  if (target) {
    return <WriteEmailDialog target={{ type: target.entityType, id: target.entityId }} reply={target} onClose={onClose} />
  }
  return (
    <EditLayer kind="modal" large={false} title={t('mail.reply.title')} onClose={onClose}
               footer={<button type="button" className="btn btn-secondary" onClick={onClose}>{t('mail.close')}</button>}>
      {error
        ? <div className="alert alert-danger" role="alert"><span>{error}</span></div>
        : <span className="t-sm muted" role="status">{t('mail.reply.loading')}</span>}
    </EditLayer>
  )
}
